import {
	json,
	type DataFunctionArgs,
	type V2_MetaFunction,
	type HeadersFunction,
} from '@remix-run/node'
import { NavLink, Outlet, useLoaderData } from '@remix-run/react'
import invariant from 'tiny-invariant'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import Avatar from '~/components/ui/avatar.tsx'
import { prisma } from '~/utils/db.server.ts'
import { useOptionalUser } from '~/utils/user.ts'
import {
	combineServerTimings,
	makeTimings,
	time,
} from '~/utils/timing.server.ts'
import { cn } from '~/utils/misc.ts'
import { Heading } from '~/components/heading.tsx'

export async function loader({ params }: DataFunctionArgs) {
	invariant(params.username, 'Missing username')
	const timings = makeTimings('notes loader')
	const user = await time(
		() =>
			prisma.user.findUnique({
				where: {
					username: params.username,
				},
				select: {
					id: true,
					username: true,
					name: true,
					imageId: true,
				},
			}),
		{ timings, type: 'find user' },
	)
	if (!user) {
		throw new Response('not found', { status: 404 })
	}
	const notes = await time(
		() =>
			prisma.note.findMany({
				where: {
					ownerId: user.id,
				},
				select: {
					id: true,
					title: true,
				},
			}),
		{ timings, type: 'find notes' },
	)
	return json(
		{ user, notes },
		{ headers: { 'Server-Timing': timings.toString() } },
	)
}

export const headers: HeadersFunction = ({ loaderHeaders, parentHeaders }) => {
	return {
		'Server-Timing': combineServerTimings(parentHeaders, loaderHeaders),
	}
}

export default function UsernameIndex() {
	const { user, notes } = useLoaderData<typeof loader>()
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isOwner = user.id === loggedInUser?.id
	const navLinkDefaultClassName = 'border-transparent'

	return (
		<>
			<header className="flex items-center justify-between gap-8">
				<div className="grid font-sans">
					<NavLink className="border-0" to={`/${user.username}`} end>
						{({ isActive }) => (
							<Heading
								as={isActive ? 'h1' : 'h2'}
								className="text-base font-medium text-foreground dark:text-offWhite"
							>
								{userDisplayName}
							</Heading>
						)}
					</NavLink>
					<p className="text-muted-foreground">
						{/* {user.bio}  */}
						Specialized Generalist
					</p>
					<p className="text-muted-foreground">
						{/* {user.location} */}
						Toronto, Canada
					</p>
				</div>
				<Avatar size="large" user={user} />
			</header>
			<section>
				<h2 className="text-foreground">Notes</h2>
				{notes.map(note => (
					<li key={note.id}>
						<NavLink
							to={`/${user.username}/notes/${note.id}`}
							className={({ isActive }) =>
								cn(
									navLinkDefaultClassName,
									isActive && 'border-b border-border',
								)
							}
						>
							{note.title}
						</NavLink>
					</li>
				))}
				{isOwner && (
					<li>
						<NavLink
							to={`/${user.username}/notes/new`}
							className={({ isActive }) =>
								cn(
									navLinkDefaultClassName,
									isActive && 'border-b border-border',
								)
							}
						>
							New Note
						</NavLink>
					</li>
				)}
			</section>
			<Outlet />
		</>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}

export const meta: V2_MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | Dossier` },
		{
			name: 'description',
			content: `Profile of ${displayName} on Dossier`,
		},
	]
}
